import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
} from "@chakra-ui/react";

// SS-4260: every Customize AI instructions save (the text, the setup assistant, the
// AI Details controls, a history restore) is checked by the api against the settings
// before anything is stored. The check takes a while, so the save runs behind a modal
// that cannot be dismissed; a refused save then shows why, and nothing was stored.
// Each conflict comes with the correct version of the quoted text (suggested_text) and,
// when one exists, the setting value that would agree with the text (suggested_setting).
// The page that saved offers to apply either one; nothing is applied or saved without
// a click, and saving again is validated again.

export const CONFIG_CONFLICT = "CONFIG_CONFLICT";
export const CONFIG_VALIDATION_UNAVAILABLE = "CONFIG_VALIDATION_UNAVAILABLE";
export const VALIDATION_UNAVAILABLE_MESSAGE =
  "We couldn't validate the configuration, so nothing was saved. Please try again.";

// True for an answer the validation modal has already explained to the user.
export const isConfigValidationError = (res) =>
  res?.type === "error" && (res?.code === CONFIG_CONFLICT || res?.code === CONFIG_VALIDATION_UNAVAILABLE);

const IDLE = { status: "idle", conflicts: [] };

const NOTHING_SAVED = "Nothing was saved. Apply a fix or edit it yourself, then save again.";
const NOTHING_RESTORED = "Nothing was restored. This version's text and settings conflict; to use it, apply the fix to your current instructions or settings and save them.";

// Replace exactly the quoted excerpt and nothing else: everywhere its words appear,
// whatever their case or spacing, as the api checked the correction (the same sentence
// conflicts the same way wherever it is). Null when the text does not hold it.
const excerptPattern = (excerpt) =>
  new RegExp(String(excerpt).trim().split(/\s+/).map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("\\s+"), "gi");
export const replaceExcerpt = (text, excerpt, replacement) => {
  const source = String(text ?? "");
  if (!excerpt || !String(excerpt).trim()) return null;
  const pattern = excerptPattern(excerpt);
  if (!pattern.test(source)) return null;
  pattern.lastIndex = 0;
  return source.replace(pattern, () => replacement);
};

// The setting change of a suggested_setting, applied the way its control writes it
// (CustomizeAIDetail.js); the api applies setting_changes the same way.
//   booking field: { field, placement: "must" | "during" | "after" | "off", only_for_new_patients }
//   anything else: { field, value }
const BOOKING_ARRAYS = { must: "booking_blocker_fields", during: "booking_required_fields", after: "booking_optional_fields" };
const SCOPE_FLAGS = {
  booking_blocker_fields: "booking_blocker_fields_only_for_new_patients",
  booking_required_fields: "booking_required_fields_only_for_new_patients",
  booking_optional_fields: "booking_optional_fields_only_for_new_patients",
};
const MEMBER_ID_KEYS = ["insurance_policy_number", "insurance_subscriber_id"];
const DEPENDS_ON = {
  insurance_policy_number: ["insurance_name"],
  insurance_group_number: ["insurance_name"],
  insurance_plan_number: ["insurance_name"],
  insurance_employer: ["insurance_name"],
  insurance_policyholder_name: ["insurance_name"],
  insurance_policyholder_birth_date: ["insurance_name", "insurance_policyholder_name"],
};
const isPlacement = (change) => !!change && Object.prototype.hasOwnProperty.call(change, "placement");

export const applySettingChange = (oi, change) => {
  const next = { ...(oi || {}) };
  if (!isPlacement(change)) {
    next[change.field] = change.value;
    return next;
  }
  const arrays = Object.values(BOOKING_ARRAYS);
  const inAny = (field) => arrays.some((a) => (next[a] || []).includes(field));
  const strip = (keys) => {
    for (const a of arrays) next[a] = (next[a] || []).filter((f) => !keys.includes(f));
  };
  const keys = MEMBER_ID_KEYS.includes(change.field) ? MEMBER_ID_KEYS : [change.field];
  strip(keys);
  const target = BOOKING_ARRAYS[change.placement];
  if (target) {
    next[target] = [...next[target], keys[0]];
    const newOnly = change.only_for_new_patients;
    if (typeof newOnly === "boolean" && !!next[SCOPE_FLAGS[target]] !== newOnly) {
      // The "during booking" card sets the blocker and required audiences together.
      const card = target === BOOKING_ARRAYS.after ? [target] : [BOOKING_ARRAYS.must, BOOKING_ARRAYS.during];
      for (const a of card) next[SCOPE_FLAGS[a]] = newOnly;
    }
  }
  for (let changed = true; changed;) {
    changed = false;
    for (const [dependent, prerequisites] of Object.entries(DEPENDS_ON)) {
      const dependentKeys = dependent === MEMBER_ID_KEYS[0] ? MEMBER_ID_KEYS : [dependent];
      if (dependentKeys.some(inAny) && !prerequisites.every(inAny)) {
        strip(dependentKeys);
        changed = true;
      }
    }
  }
  return next;
};

// A fix from the conflict modal that the page saves with its next save:
//   { change: suggested_setting, parameter, value } or { text_excerpt, suggested_text }.
// Both are kept in order and applied one after the other to what is stored, as clicks on
// the controls (turning insurance off also turns off its details) or edits of the text
// would be.
export const addPendingSetting = (pending, conflict) => [
  ...(pending || []),
  { change: conflict.suggested_setting, parameter: conflict.parameter, value: conflict.suggested_setting_value },
];
export const addPendingWording = (pending, conflict) => {
  const list = pending || [];
  const last = list[list.length - 1];
  // Only a repeat of the change just made is dropped; an earlier one may have been undone since.
  return last && last.text_excerpt === conflict.text_excerpt && last.suggested_text === conflict.suggested_text
    ? list
    : [...list, { text_excerpt: conflict.text_excerpt, suggested_text: conflict.suggested_text }];
};

// What the next save will also change, so a fix applied from the modal is visible.
export const PendingFixesNote = ({ settings = [], wording = [], what }) => {
  if (!settings.length && !wording.length) return null;
  // Each setting once, at the value it will have.
  const latest = [...new Map(settings.map((p) => [p.change.field, p])).values()];
  return (
    <Box mt={2} p={2} borderWidth="1px" borderRadius="md" borderColor="blue.200" bg="blue.50" color="blue.900" width="full" data-testid="config-pending-fixes">
      <Text fontSize="sm" fontWeight="bold">Saved with {what} when you save:</Text>
      {latest.map((p) => (
        <Text fontSize="sm" key={`s-${p.change.field}`}>{p.parameter}: {p.value}</Text>
      ))}
      {wording.map((p, i) => (
        <Text fontSize="sm" key={`w-${i}`}>&ldquo;{p.text_excerpt}&rdquo; &rarr; &ldquo;{p.suggested_text}&rdquo;</Text>
      ))}
    </Box>
  );
};

const CustomizeAIValidationModal = ({ status, conflicts = [], onClose, onUseWording, onUseSetting }) => {
  // Which fixes were applied in this modal, and which wording could not be found any more.
  const [applied, setApplied] = useState({});
  const [notFound, setNotFound] = useState({});
  useEffect(() => {
    setApplied({});
    setNotFound({});
  }, [conflicts]);
  // Applying a fix changes the form only; the page saves when the user saves. Conflicts
  // quoting the same words share one correction, so applying it applies it for all of them.
  const apply = (key, handler, conflict) => {
    const index = conflicts.indexOf(conflict);
    const ok = handler(conflict) !== false;
    const isText = key.startsWith("text-");
    const keys = isText
      ? conflicts.map((c, i) => (c.text_excerpt === conflict.text_excerpt ? `text-${i}` : null)).filter(Boolean)
      : [key];
    setApplied((prev) => ({ ...prev, ...Object.fromEntries(keys.map((k) => [k, ok])) }));
    setNotFound((prev) => ({ ...prev, [index]: !ok && isText }));
  };

  if (status === "validating") {
    // No close button, overlay click or Escape, and focus stays inside: the save is still
    // running. A restore first asks for confirmation in cqAPI's dialog, which opens above
    // this one; the focus lock is shared, so that dialog holds focus while it is open and
    // focus comes back here when it closes.
    return (
      <Modal isOpen onClose={() => {}} closeOnOverlayClick={false} closeOnEsc={false} isCentered>
        <ModalOverlay/>
        <ModalContent data-testid="config-validating-modal">
          <ModalHeader>Validating the configuration...</ModalHeader>
          <ModalBody pb={6}>
            <Flex justifyContent="center">
              <Spinner size="lg"/>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  if (status === "conflict") {
    return (
      <Modal isOpen onClose={onClose} size="xl" scrollBehavior="inside">
        <ModalOverlay/>
        <ModalContent data-testid="config-conflict-modal">
          <ModalHeader>Your instructions conflict with your settings</ModalHeader>
          <ModalCloseButton/>
          <ModalBody>
            {conflicts.map((conflict, i) => (
              <Box key={i} borderWidth="1px" borderRadius="md" p={3} mb={3} data-testid="config-conflict">
                <Text fontSize="sm" fontWeight="bold">
                  {conflict.parameter}{conflict.parameter_value ? `: ${conflict.parameter_value}` : ""}
                </Text>
                {conflict.text_excerpt && (
                  <Text fontSize="sm" mt={2} pl={3} borderLeftWidth="3px" borderLeftColor="yellow.400" fontStyle="italic">
                    &ldquo;{conflict.text_excerpt}&rdquo;
                  </Text>
                )}
                {conflict.explanation && (
                  <Text fontSize="sm" mt={2}>{conflict.explanation}</Text>
                )}
                {conflict.suggested_text && (
                  <Box mt={3} data-testid="config-conflict-fix">
                    <Text fontSize="sm" fontWeight="bold">The correct version</Text>
                    <Text fontSize="sm" mt={1} p={2} borderRadius="md" bg="red.50" color="red.800" textDecoration="line-through" data-testid="config-conflict-before">
                      {conflict.text_excerpt}
                    </Text>
                    <Text fontSize="sm" mt={1} p={2} borderRadius="md" bg="green.50" color="green.800" data-testid="config-conflict-after">
                      {conflict.suggested_text}
                    </Text>
                    {onUseWording && (
                      <Button size="sm" mt={2} isDisabled={!!applied[`text-${i}`]} onClick={() => apply(`text-${i}`, onUseWording, conflict)}>
                        {applied[`text-${i}`] ? "Wording applied" : "Use this wording"}
                      </Button>
                    )}
                  </Box>
                )}
                {conflict.suggested_setting && (
                  <Flex mt={3} alignItems="center" gap={2} flexWrap="wrap" data-testid="config-conflict-setting">
                    <Text fontSize="sm">
                      Or change the setting to &ldquo;{conflict.suggested_setting_value || "the value your text describes"}&rdquo;
                    </Text>
                    {onUseSetting && (
                      <Button size="sm" variant="outline" isDisabled={!!applied[`setting-${i}`]} onClick={() => apply(`setting-${i}`, onUseSetting, conflict)}>
                        {applied[`setting-${i}`] ? "Setting changed" : "Change the setting"}
                      </Button>
                    )}
                  </Flex>
                )}
                {notFound[i] && (
                  <Text fontSize="sm" mt={2} color="red.600">
                    The quoted text is no longer in your instructions; edit it yourself.
                  </Text>
                )}
              </Box>
            ))}
            <Text fontSize="sm" fontWeight="bold" mt={2}>
              {onUseWording || onUseSetting ? NOTHING_SAVED : NOTHING_RESTORED}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  if (status === "unavailable") {
    return (
      <Modal isOpen onClose={onClose} size="lg">
        <ModalOverlay/>
        <ModalContent data-testid="config-unavailable-modal">
          <ModalHeader>Nothing was saved</ModalHeader>
          <ModalCloseButton/>
          <ModalBody>
            <Text fontSize="sm">{VALIDATION_UNAVAILABLE_MESSAGE}</Text>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  return null;
};

// Runs one instructions save behind the "Validating the configuration..." modal and
// returns the api's answer unchanged. A CONFIG_CONFLICT or CONFIG_VALIDATION_UNAVAILABLE
// answer replaces the modal with the error; the caller keeps its form as it is.
// The caller says what its fixes do in its form:
//   onUseWording(conflict): put conflict.suggested_text in place of conflict.text_excerpt
//     (return false when the text no longer holds the excerpt);
//   onUseSetting(conflict): set the control to conflict.suggested_setting.
// Without them (a history restore) the fixes are shown, not offered.
export const useConfigValidation = ({ onUseWording, onUseSetting } = {}) => {
  const [state, setState] = useState(IDLE);
  // The latest handlers, so a fix acts on the form as it is when clicked.
  const handlers = useRef({});
  handlers.current = { onUseWording, onUseSetting };
  const applyWording = useCallback((conflict) => handlers.current.onUseWording?.(conflict), []);
  const applySetting = useCallback((conflict) => handlers.current.onUseSetting?.(conflict), []);

  const runValidatedSave = useCallback(async (save) => {
    setState({ status: "validating", conflicts: [] });
    let res;
    try {
      res = await save();
    } catch (e) {
      setState(IDLE);
      throw e;
    }
    if (res?.type === "error" && res?.code === CONFIG_CONFLICT) {
      setState({ status: "conflict", conflicts: Array.isArray(res.conflicts) ? res.conflicts : [] });
    } else if (res?.type === "error" && res?.code === CONFIG_VALIDATION_UNAVAILABLE) {
      setState({ status: "unavailable", conflicts: [] });
    } else {
      setState(IDLE);
    }
    return res;
  }, []);

  const close = useCallback(() => setState(IDLE), []);

  return {
    runValidatedSave,
    validationModal: (
      <CustomizeAIValidationModal
        status={state.status}
        conflicts={state.conflicts}
        onClose={close}
        onUseWording={onUseWording ? applyWording : undefined}
        onUseSetting={onUseSetting ? applySetting : undefined}
      />
    ),
  };
};

export default CustomizeAIValidationModal;
